import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';
import { v4 } from 'uuid';

import { DashboardApi, DashboardDetailWidgetData, DashboardDetailWidgetLayout, DashboardDetailWidgetPosition } from '../dashboard-api';
import { DashboardDetail } from './dashboard-detail';
import { DashboadWidgetRename } from './dashboard-widget-rename';
import { DashboadWidgetSelectApp } from './dashboard-widget-select-app';

export interface DashboardDetailWidget extends DashboardDetailWidgetData {
  selectApp: DashboadWidgetSelectApp | undefined;
  rename: DashboadWidgetRename | undefined;
  mobilePosition: DashboardDetailWidgetPosition;
}

function emptyPosition(): DashboardDetailWidgetPosition {
  return { colStart: 0, nCols: 0, rowStart: 0, nRows: 0 };
}

export function dashboardDetailWidgetsFromData(
  oldWidgets: DashboardDetailWidget[],
  newWidgets: DashboardDetailWidgetData[]
): DashboardDetailWidget[] {
  return newWidgets.map((data) => {
    const old = oldWidgets.find((w) => w.id === data.id);
    return { ...data, selectApp: old?.selectApp, rename: old?.rename, mobilePosition: emptyPosition() };
  });
}

export function emptyDashboardDetailWidget(rowStart: number): DashboardDetailWidget {
  return {
    id: v4(),
    name: undefined,
    serviceId: undefined,
    serviceName: undefined,
    serviceStatus: 'STOPPED',
    serviceUrl: undefined,
    serviceType: undefined,
    colStart: 0,
    nCols: 3,
    rowStart,
    nRows: 3,
    selectApp: undefined,
    rename: undefined,
    mobilePosition: emptyPosition(),
  };
}

export function updateDashboardDetailWidgetsMobilePosition(widgets: DashboardDetailWidget[]): DashboardDetailWidget[] {
  const copy = widgets.map((w) => ({ ...w }));
  copy.sort((a, b) => {
    const cmpRow = a.rowStart - b.rowStart;
    if (cmpRow === 0) {
      return a.colStart - b.colStart;
    }

    return cmpRow;
  });

  let nextRow = 0;
  for (const widget of copy) {
    widget.mobilePosition = {
      colStart: 0,
      nCols: 12,
      rowStart: nextRow,
      nRows: widget.nRows,
    };

    nextRow += widget.nRows;
  }

  return copy;
}

export async function addDashboardEmptyWidget(api: DashboardApi, getModel: () => DashboardDetail, update: (_: DashboardDetail) => void) {
  const model = getModel();
  const rowStart = model.widgets.reduce((acc, w) => Math.max(w.rowStart + w.nRows, acc), 0);

  const newWidget = emptyDashboardDetailWidget(rowStart);

  update({ ...model, widgets: updateDashboardDetailWidgetsMobilePosition([...model.widgets, newWidget]) });

  const res = await api.createWidget(model.id, pick(newWidget, ['id', 'colStart', 'nCols', 'rowStart', 'nRows']));

  if (!res?.ok) {
    return { message: 'Error creating widget.' };
  }
}

export async function removeDashboardWidget(api: DashboardApi, getModel: () => DashboardDetail, update: (_: DashboardDetail) => void) {
  const model = getModel();
  const id = model.widgetToRemove?.id;
  if (!id) {
    return;
  }

  const widgets = updateDashboardDetailWidgetsMobilePosition(model.widgets.filter((w) => w.id !== id));

  update({ ...model, widgets, widgetToRemove: undefined });

  const res = await api.deleteWidget(model.id, id);

  if (!res?.ok) {
    return { message: 'Error removing widget.' };
  }
}

export async function updateDashboardWidgetPositions(
  positions: DashboardDetailWidgetLayout[],
  api: DashboardApi,
  getModel: () => DashboardDetail,
  update: (_: DashboardDetail) => void
) {
  const model = getModel();
  const newPositions: DashboardDetailWidgetLayout[] = [];
  const positionById = new Map(positions.map((pos) => [pos.id, pos]));

  const widgets = model.widgets.map((w) => {
    const oldPos = pick(w, ['id', 'colStart', 'nCols', 'rowStart', 'nRows']);
    const newPos = positionById.get(w.id);

    if (!newPos || isEqual(oldPos, newPos)) {
      return w;
    }

    newPositions.push(newPos);

    return { ...w, ...newPos };
  });

  update({ ...model, widgets });

  const promises = newPositions.map((p) => {
    const { id, ...position } = p;
    return api.updateWidget(model.id, id, position);
  });

  const res = await Promise.all(promises);

  if (res.some((r) => !r?.ok)) {
    return { message: 'Error updating widget.' };
  }
}
